import {
    fontFamilyDefault,
    spacing40,
    spacing50,
    spacingInset50,
    spacing30,
    colorCtaBlueHover
} from '@ellucian/react-design-system/core/styles/tokens';
import React, { useEffect, useContext, useState, useMemo } from 'react';
import {
    Typography,
    Button,
    Paper,
    Illustration,
    IMAGES,
    Divider,
    TextLink,
    useMediaQuery,
    makeStyles
} from '@ellucian/react-design-system/core';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import get from 'lodash/get';
import { useTheme } from '@mui/material';
import { useApi } from '../ApiContext';
import { routes, CONSTANTS } from '../Helpers/Configuration';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import GlobalLocaleContext from '../GlobalLocale/GlobalLocaleContext';
import { useDispatch } from 'react-redux';
import isLoadingReducer, { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice.js';
import { Form as ReduxStateStore } from '@nsr/react-form';
import PageLoadIndicator from '../Helpers/PageLoadIndicator';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
const useStyles = makeStyles((theme) => ({
    root: {
        padding: spacing50,
        width: '100%'
    },
    container: {
        padding: [spacingInset50, '!important'],
        marginTop: spacing40,
        marginBottom: spacing40
    },
    application: {
        display: 'grid',
        gridTemplateColumns: '1fr',
    },
    noApplications: {
        display: 'grid',
        gridGap: spacing50,
        padding: spacing50,
    },
    startButton: {
        width: '100%',
    },
    description: {
        fontFamily: fontFamilyDefault,
        marginBottom: spacing30,
    },
    applicationTypography: {
        marginBottom: spacing30,
    },
    [theme.breakpoints.up('sm')]: {
        container: {
            padding: spacing40,
            marginTop: spacing50,
            marginBottom: spacing50
        },
        application: {
            display: 'grid',
            gridTemplateColumns: '1fr minmax(100px, max-content)',
        },
        description: {
            marginBottom: 'unset',
        },
    },
    backIcon: {
        height: ['11px', '!important'],
        color: colorCtaBlueHover,
    },
    backLinkContainer: {
        marginBottom: spacing40,
        marginTop: spacing50
    }
}));

const NewApplicationWrapper = props => {
    const additionalSlices = useMemo(() => ({
        isLoading: isLoadingReducer,
    }), []);
    return (
        <ReduxStateStore additionalSlices={additionalSlices}>
            <NewApplication {...props} />
        </ReduxStateStore>
    );
};

const NewApplication = () => {
    const classes = useStyles();
    const dispatch = useDispatch();
    let templates = get(window, 'PRELOADED_STATE.templates', []);
    const api = useApi();
    const history = useHistory();
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { globalLocale, localeDirection } = useContext(GlobalLocaleContext);
    const language = useMemo(
        () => globalLocale ? globalLocale : get(window, 'PRELOADED_STATE.globalSettings.defaultLanguage', 'en-US'),
        [globalLocale],
    );
    const [, setUpdate] = useState(false);
    const createApplication = async originalTemplateId => {
        dispatch(startLoading());
        const latestTemplate = await api.applicationTemplates.getLatestPublishedTemplate(originalTemplateId);
        const result = await api.applicationForms.create(latestTemplate.id);
        dispatch(stopLoading());
        await history.push(routes.applicationFormStartWithId(result.id));
        return result;
    };
    useEffect(() => {
        if (templates && templates.length > 0) {
            // get localized labels
            templates.forEach(it => {
                const { defaultLanguage, externalDescription, name } = it;
                it.nameText = name && (name[language] ?? name[defaultLanguage]);
                it.descriptionText = externalDescription && (externalDescription[language] ?? externalDescription[defaultLanguage]);
            });
            // sort
            templates?.sort(function (a, b) {
                return new Intl.Collator(language, { sensitivity: 'base' }).compare(a.nameText, b.nameText);
            });
            setUpdate((x) => !x);
        }
    }, [language]);

    return (
        <div className={classes.root}>
            <PageLoadIndicator />
            <div className={classes.backLinkContainer}>
                <Icon name="chevron-left" className={classes.backIcon} dir={localeDirection} />
                <TextLink
                    href={'#0'}
                    onClick={(e) => { e?.preventDefault(); history.push(routes.home); }}
                >
                    {formatMessage({ id: 'applicationBackToApplicationsHome' })}
                </TextLink>
            </div>
            <Typography variant="h2"><FormattedMessage id="applicantNewApplicationAvailableForms" /></Typography>
            <Paper className={classes.container}>
                <>
                    {templates.map(it => (
                        <div key={it.id}>
                            { it.applicationTemplateType != CONSTANTS.applicationTemplateTypes.import &&
                                <>
                                    {!isMobile &&
                                        <div className={classes.application}>
                                            <div>
                                                <Typography className={classes.applicationTypography} variant="h3" >
                                                    {it.nameText}
                                                </Typography>
                                                <div className={classes.description}>
                                                    <HtmlFormattedMessage htmlString={it.descriptionText} />
                                                </div>
                                            </div>
                                            <div>
                                                <Button color="secondary" aria-label={`${formatMessage({ id: 'applicantHomeStart' })} ${it.nameText}`} onClick={() => createApplication(it.originalTemplateId)} className={classes.startButton}>
                                                    {formatMessage({ id: 'applicantHomeStart' })}
                                                </Button>
                                            </div>
                                        </div>
                                    }
                                    {isMobile &&
                                        <div className={classes.application}>
                                            <Typography className={classes.applicationTypography} variant="h3" >
                                                {it.nameText}
                                            </Typography>
                                            <div className={classes.description}>
                                                <HtmlFormattedMessage htmlString={it.descriptionText} />
                                            </div>
                                            <div>
                                                <Button color="secondary" aria-label={`${formatMessage({ id: 'applicantHomeStart' })} ${it.nameText}`} onClick={() => createApplication(it.originalTemplateId)} className={classes.startButton}>
                                                    {formatMessage({ id: 'applicantHomeStart' })}
                                                </Button>
                                            </div>
                                        </div>
                                    }
                                    <Divider />
                                </>
                            }
                        </div>
                    ))}
                    {templates?.length === 0 &&
                        <div className={classes.noApplications}>
                            <Illustration name={IMAGES.NO_SEARCH_RESULTS} />
                            <Typography variant="h3"><FormattedMessage id="applicantNewApplicationEmptyList" /></Typography>
                        </div>
                    }
                </>
            </Paper>
        </div>
    );
};

export default NewApplicationWrapper;
